import { USER_ACTIONS } from '@hotjar/common';
import type { SectionInfo, PageVisitRecording } from '../types';

// Note 1: When a time is in percent, it is a percentage from the total duration of the recording.
// Note 2: We only create inactivity section if:
// - is longer than 3s if 3s is bigger than 1% of the recording duration.
// - is longer than 1% of the recording duration otherwise
// It means that we will still skip smaller durations even if there are not shown on the timeline

const IDEAL_MIN_DURATION_FOR_SHOWING_INACTIVITY_IN_MS = 3000;
const MIN_DURATION_FOR_SHOWING_INACTIVITY_IN_PERCENT = 1;

export const IDEAL_DELAY_AFTER_INACTIVITY_IN_MS = 2000;

interface AddInactivitySectionContext {
  pageIndex: number;
  pageVisit: PageVisitRecording;
  sectionInfoList: SectionInfo[];
}

interface AddSectionInfoContext {
  pageIndex: number;
  pageVisit: PageVisitRecording;
  sectionInfoList: SectionInfo[];
}

export class SectionBuilder {
  private recordingDuration: number;

  private minDurationForInactivityInMs: number;

  private delayAfterInactivityInMs: number;

  constructor(
    recordingDuration: number,
    idealMinDurationForInactivityInMs = IDEAL_MIN_DURATION_FOR_SHOWING_INACTIVITY_IN_MS,
    idealDelayAfterInactivityInMs = IDEAL_DELAY_AFTER_INACTIVITY_IN_MS,
  ) {
    this.recordingDuration = recordingDuration;
    const idealMinDurationForInactivityInPercent = this.toPercent(
      idealMinDurationForInactivityInMs,
    );

    // We want to use the ideal duration but if it's smaller than 1% of the recording (= long recording), we use 1%.
    this.minDurationForInactivityInMs =
      idealMinDurationForInactivityInPercent > MIN_DURATION_FOR_SHOWING_INACTIVITY_IN_PERCENT
        ? idealMinDurationForInactivityInMs
        : this.toMs(MIN_DURATION_FOR_SHOWING_INACTIVITY_IN_PERCENT);

    // minDurationForInactivityInMs should always be bigger than 2 * delayAfterInactivityInMs
    // Because we substract delayAfterInactivityInMs at the end of the inactivity
    // In case the minDurationForInactivityInMs is too small, we just divide it by 2
    this.delayAfterInactivityInMs =
      idealDelayAfterInactivityInMs * 2 <= this.minDurationForInactivityInMs
        ? idealDelayAfterInactivityInMs
        : this.minDurationForInactivityInMs / 2;
  }

  generateSectionInfoList = (pageVisitRecordings: PageVisitRecording[]): SectionInfo[] => {
    const userActionsTypes = Object.values(USER_ACTIONS);
    const sectionInfoList: SectionInfo[] = [];
    let timeCursorInMs = 0;

    pageVisitRecordings.forEach((pageVisit, pageIndex) => {
      timeCursorInMs = pageVisit.time_in_timeline;

      const userActionEventList = pageVisit.events.filter((item) =>
        userActionsTypes.includes(item.type),
      );
      // Check each user action events to see if we should introduce an inactivity section.
      // eslint-disable-next-line no-restricted-syntax
      for (const currentEvent of userActionEventList) {
        const deltaPendingSectionCurrentEvent = currentEvent.time_in_timeline - timeCursorInMs;

        if (deltaPendingSectionCurrentEvent > this.minDurationForInactivityInMs) {
          // We found an inactivity section
          const context = {
            sectionInfoList,
            pageIndex,
            pageVisit,
          };
          this.addInactivitySection(timeCursorInMs, currentEvent.time_in_timeline, context);
        }

        // We update the timeCursorInMs to be able to detect the inactivity after this event
        timeCursorInMs = currentEvent.time_in_timeline;
      }

      // One thing which is not an event but is important is the end of the page.
      // We should treat as if it was an event to detect inactivity in that section as well
      const deltaPendingSectionCurrentEvent =
        pageVisit.time_in_timeline + pageVisit.duration - timeCursorInMs;

      if (deltaPendingSectionCurrentEvent > this.minDurationForInactivityInMs) {
        // We found an inactivity section
        const context = {
          sectionInfoList,
          pageIndex,
          pageVisit,
        };
        this.addInactivitySection(
          timeCursorInMs,
          pageVisit.time_in_timeline + pageVisit.duration,
          context,
        );

        return;
      }

      // Each page finishes with an active section.
      const isActive = true;
      const endTimeInMs = pageVisit.time_in_timeline + pageVisit.duration;
      const context = { sectionInfoList, pageIndex, pageVisit };
      this.addSectionInfo(endTimeInMs, isActive, context);
    });

    return sectionInfoList;
  };

  addSectionInfo = (
    endTimeInMs: number,
    isActive: boolean,
    { sectionInfoList, pageIndex, pageVisit }: AddSectionInfoContext,
  ): void => {
    const previousSectionInfo: SectionInfo | null =
      sectionInfoList.length === 0 ? null : sectionInfoList[sectionInfoList.length - 1];

    const commonSectionInfo = {
      index: sectionInfoList.length,
      pageIndex,
      url: pageVisit.page_url,
      tabIndex: pageVisit.tab_index,
      isActive,
    };
    let newSectionInfo;

    if (previousSectionInfo) {
      const startTimeInMs = previousSectionInfo.startTimeInMs + previousSectionInfo.durationInMs;
      const durationInMs = endTimeInMs - startTimeInMs;
      newSectionInfo = {
        startTimeInMs,
        startTimeInPercent: this.toPercent(startTimeInMs),
        durationInMs,
        durationInPercent: this.toPercent(durationInMs),
      };
    } else {
      newSectionInfo = {
        startTimeInMs: 0,
        startTimeInPercent: this.toPercent(0),
        durationInMs: endTimeInMs,
        durationInPercent: this.toPercent(endTimeInMs),
      };
    }
    sectionInfoList.push({ ...newSectionInfo, ...commonSectionInfo });
  };

  addInactivitySection = (
    inactivityStartTime: number,
    inactivityStopTimeWithoutDelay: number,
    { sectionInfoList, pageIndex, pageVisit }: AddInactivitySectionContext,
  ): void => {
    const inactivityEndTime = Math.max(
      inactivityStartTime,
      inactivityStopTimeWithoutDelay === pageVisit.time_in_timeline + pageVisit.duration
        ? inactivityStopTimeWithoutDelay
        : inactivityStopTimeWithoutDelay - this.delayAfterInactivityInMs,
    );

    // We add the previous active pending section
    let isActive = true;
    const context = { sectionInfoList, pageIndex, pageVisit };
    this.addSectionInfo(inactivityStartTime, isActive, context);

    // We create the inactive section
    isActive = false;
    this.addSectionInfo(inactivityEndTime, isActive, context);
  };

  toPercent = (timeInMs: number): number => {
    return (timeInMs / this.recordingDuration) * 100;
  };

  toMs = (timeInPercent: number): number => {
    return (timeInPercent / 100) * this.recordingDuration;
  };
}
